import { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";

import starImage from "../../../assets/star.png";
import useUserType from "../../../hooks/useUserType";
import CustomModal from "../../CustomModal";

const MemoryGameFinishModal = forwardRef(({ onSubmit, onSubmitReset }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [result, setResult] = useState({
    timeSpent: 0,
    moves: 0,
    didTimeRunOut: false,
  });

  const { isStudent } = useUserType();
  const { t } = useTranslation();

  useImperativeHandle(ref, () => ({
    openModal: (data) => {
      setResult(data);
      setIsOpen(true);
    },
    closeModal: () => setIsOpen(false),
  }));

  const handleReset = () => {
    onSubmitReset();
    setIsOpen(false);
  };

  const handleConfirm = () => {
    onSubmit();
    setIsOpen(false);
  };

  const getStarRating = () => {
    const { timeSpent, moves, didTimeRunOut } = result;
    if (didTimeRunOut) return 0;
    if (timeSpent <= 30 && moves <= 20) return 3;
    if (timeSpent <= 45 && moves <= 30) return 2;
    if (timeSpent <= 120 && moves <= 40) return 1;
    return 0;
  };

  const score = getStarRating();

  const getScoreMessage = () => {
    if (result.didTimeRunOut) return t("memory_game_finish.times_up");
    const messages = [
      t("memory_game_finish.try_again"),
      t("memory_game_finish.good_job"),
      t("memory_game_finish.great_job"),
      t("memory_game_finish.amazing_job"),
    ];
    return messages[score];
  };

  const renderStars = () =>
    [...Array(score)].map((_, i) => <img key={i} src={starImage} alt="star" className="h-12 w-12" />);

  const renderActionButtons = () => {
    const commonButtonClass =
      "wavy-btn relative w-1/2 rounded-3xl border-4 border-[#ffe4af] bg-orange-500 p-4 text-lg font-semibold text-white transition duration-200";

    if (score === 0) {
      return (
        <button onClick={handleReset} className={commonButtonClass}>
          {t("play_again")}
        </button>
      );
    }

    return (
      <div className="flex w-full items-center justify-between gap-8">
        <button onClick={handleReset} className={commonButtonClass}>
          {t("play_again")}
        </button>
        <button onClick={isStudent ? handleConfirm : () => setIsOpen(false)} className={commonButtonClass}>
          {isStudent ? t("submit") : t("close")}
        </button>
      </div>
    );
  };

  return (
    <CustomModal
      isOpen={isOpen}
      withCloseButton={false}
      onClose={() => setIsOpen(false)}
      className="max-w-[40rem] rounded-2xl border-8 border-blue-500 bg-[#F1F1F1] shadow-xl"
      contentClassname="px-2 py-6 md:px-10"
    >
      <h2 className="mb-6 text-center text-3xl font-bold text-blue-900">{getScoreMessage()}</h2>

      <p className="mb-2 text-center text-gray-700">
        {t("memory_game_finish.result_message_1")}
        <br />
        <span className="text-2xl font-bold text-blue-900">
          {result.timeSpent} {t("memory_game_finish.seconds")} 🎆
        </span>
        <br />
        {t("memory_game_finish.result_message_2")}
        <span className="text-2xl font-bold text-blue-900">
          {" "}
          {result.moves} {t("memory_game_finish.moves")} 🤖
        </span>
      </p>

      <div className="mb-6 flex justify-center gap-3">{renderStars()}</div>
      <div className="flex justify-center gap-6">{renderActionButtons()}</div>
    </CustomModal>
  );
});

export default MemoryGameFinishModal;
